import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faFileCsv,
  faFilePdf,
  faPrint,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import patientService from '../../services/patientService';
import practiceService from '../../services/practiceService';
import measurementService from '../../services/measurementService';
import { toast } from 'react-toastify';
import pulseIcon from '../../assets/pulse.png';
import './PatientDashboard.css';

const VITALS_HISTORY_ROW_LIMIT = 25;
const BP_PERIODS = [
  { name: 'Wake-up', start: '06:01 AM', end: '8:00 AM' },
  { name: 'Morning', start: '08:01 AM', end: '11:00 AM' },
  { name: 'Noon', start: '11:01 AM', end: '2:00 PM' },
  { name: 'Afternoon', start: '2:01 PM', end: '6:00 PM' },
  { name: 'Evening', start: '6:01 PM', end: '6:00 AM' }
];
const BG_PERIODS = [
  { name: 'Wake-up', start: '06:01 AM', end: '8:00 AM' },
  { name: 'Morning', start: '08:01 AM', end: '11:00 AM' },
  { name: 'Noon', start: '11:01 AM', end: '2:00 PM' },
  { name: 'Afternoon', start: '2:01 PM', end: '6:00 PM' },
  { name: 'Evening', start: '6:01 PM', end: '6:00 AM' }
];
const WT_PERIODS = [
  { name: 'Morning', start: '06:00 AM', end: '12:00 PM' },
  { name: 'Afternoon', start: '12:01 PM', end: '6:00 PM' },
  { name: 'Evening', start: '6:01 PM', end: '11:59 PM' }
];

const isLockedPracticeStatus = (status) => {
  if (status === undefined || status === null) return false;
  const n = Number(status);
  if (!Number.isNaN(n) && n === 4) return true;
  const t = String(status).trim().toLowerCase();
  return t === '4' || t === 'locked' || t === 'lost';
};

const PatientDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { clearBreadcrumbs } = useBreadcrumb();

  // ── Core dashboard state ────────────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── IDs resolved once; reused by lazy loaders ───────────────────────────────
  const [practiceId, setPracticeId] = useState(null);
  const [patientId, setPatientId] = useState(null);

  // ── Notes (always visible; load when IDs are known) ─────────────────────────
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  const [practiceIsLocked, setPracticeIsLocked] = useState(false);
  const [vitalsTab, setVitalsTab] = useState('bp');
  const [historyBp, setHistoryBp] = useState([]);
  const [historyBg, setHistoryBg] = useState([]);
  const [historyWt, setHistoryWt] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [patientProfile, setPatientProfile] = useState(null);

  // ── Breadcrumb ───────────────────────────────────────────────────────────────
  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  // ── Main fetch: single API call replaces 4 sequential calls ─────────────────
  useEffect(() => {
    if (user?.id) fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      // Resolve IDs
      let pid = user.practice_id || user.practices?.[0]?.id;
      let uid = user.patient_id;

      if (!pid || !uid) {
        const res = await patientService.getCurrentPatient();
        if (res.data?.success && res.data.data) {
          pid = res.data.data.practice_id;
          uid = res.data.data.id;
        } else {
          throw new Error('Patient record not found. Please contact your healthcare provider.');
        }
      }

      if (!pid || !uid) throw new Error('Patient information not found.');

      setPracticeId(pid);
      setPatientId(uid);

      // 🚀 SINGLE fast call — replaces getPatientDetails + 3 vitals calls
      const res = await patientService.getDashboardSummary(pid, uid);
      if (res.data?.success) {
        setSummary(res.data.data);
      } else {
        throw new Error('Failed to load dashboard');
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to load patient data';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    practiceService
      .getPractice(practiceId)
      .then((res) => {
        if (cancelled) return;
        const st = res?.data?.data?.status;
        setPracticeIsLocked(isLockedPracticeStatus(st));
      })
      .catch(() => {
        if (!cancelled) setPracticeIsLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId || !patientId) return;
    let cancelled = false;
    (async () => {
      try {
        setNotesLoading(true);
        const res = await patientService.getPatientNotes(practiceId, patientId);
        if (cancelled) return;
        if (res.data?.success) {
          setNotes(res.data.data || []);
        }
      } catch (err) {
        console.error('Notes load error:', err);
        if (!cancelled) toast.error('Failed to load notes');
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!practiceId || !patientId) return;
    let cancelled = false;
    patientService
      .getPatientDetailsFast(practiceId, patientId)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.success) {
          setPatientProfile(res.data.data?.patient || null);
        }
      })
      .catch(() => {
        if (!cancelled) setPatientProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!practiceId || !patientId) return;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    const from = start.toISOString().split('T')[0];
    const to = end.toISOString().split('T')[0];
    let cancelled = false;
    (async () => {
      try {
        setHistoryLoading(true);
        const [bpRes, bgRes, wtRes] = await Promise.all([
          measurementService.getBloodPressureByDateRange(practiceId, patientId, from, to, 'date').catch(() => ({ data: {} })),
          measurementService.getBloodGlucoseByDateRange(practiceId, patientId, from, to, 'date').catch(() => ({ data: {} })),
          measurementService.getWeightByDateRange(practiceId, patientId, from, to, 'date').catch(() => ({ data: {} }))
        ]);
        if (cancelled) return;
        setHistoryBp(bpRes.data?.success ? bpRes.data.data || [] : []);
        setHistoryBg(bgRes.data?.success ? bgRes.data.data || [] : []);
        setHistoryWt(wtRes.data?.success ? wtRes.data.data || [] : []);
      } catch (e) {
        console.error('Vitals history load error:', e);
        if (!cancelled) {
          setHistoryBp([]);
          setHistoryBg([]);
          setHistoryWt([]);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]}, ${String(d.getDate()).padStart(2, '0')} ${d.getFullYear()}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const normalizePeriod = (value) => String(value || '').toLowerCase().replace(/[^a-z]/g, '');

  const resolvePeriodByTime = (timeString, periods) => {
    if (!timeString || !periods?.length) return periods?.[0]?.name || '';
    const parsed = new Date(`2000-01-01 ${timeString}`);
    if (Number.isNaN(parsed.getTime())) return periods[0].name;
    const mins = parsed.getHours() * 60 + parsed.getMinutes();
    for (const p of periods) {
      const s = new Date(`2000-01-01 ${p.start}`);
      const e = new Date(`2000-01-01 ${p.end}`);
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) continue;
      const sm = s.getHours() * 60 + s.getMinutes();
      const em = e.getHours() * 60 + e.getMinutes();
      if (sm <= em) {
        if (mins >= sm && mins <= em) return p.name;
      } else if (mins >= sm || mins <= em) {
        return p.name;
      }
    }
    return periods[0].name;
  };

  const getPeriodizedRows = (rows, periods) => {
    const map = {};
    rows.forEach((r) => {
      const date = r.measurement_date || '-';
      const periodFromRow = r.period_name || r.period;
      const period = periodFromRow
        ? periods.find((p) => normalizePeriod(p.name) === normalizePeriod(periodFromRow))?.name || periodFromRow
        : resolvePeriodByTime(r.measurement_time, periods);
      if (!map[date]) map[date] = {};
      if (!map[date][period]) map[date][period] = [];
      map[date][period].push(r);
    });
    const dates = Object.keys(map).sort((a, b) => new Date(b) - new Date(a));
    return { dates, map };
  };

  const formatDateTime = (datetimeString) => {
    if (!datetimeString) return '';
    const d = new Date(datetimeString);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'PM' : 'AM';
    return `${mm}-${dd}-${yyyy}, ${hh}:${min} ${ampm}`;
  };

  const formatDateCell = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return dateString;
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const getReadingBandClass = (value, type, subtype) => {
    if (value == null || Number.isNaN(Number(value))) return 'patient-dashboard-reading-within';
    const v = Number(value);
    
    if (type === 'bp') {
      if (subtype === 'sys') {
        if (v > 140) return 'patient-dashboard-reading-high';
        if (v < 100) return 'patient-dashboard-reading-low';
      } else if (subtype === 'dia') {
        if (v > 90) return 'patient-dashboard-reading-high';
        if (v < 60) return 'patient-dashboard-reading-low';
      }
    } else if (type === 'bg') {
      if (v > 110) return 'patient-dashboard-reading-high';
      if (v < 60) return 'patient-dashboard-reading-low';
    } else if (type === 'weight') {
      if (v > 220) return 'patient-dashboard-reading-high';
      if (v < 66) return 'patient-dashboard-reading-low';
    }
    
    return 'patient-dashboard-reading-within';
  };

  const getBandClass = (band) => {
    if (band === 'high') return 'patient-dashboard-reading-high';
    if (band === 'low') return 'patient-dashboard-reading-low';
    return 'patient-dashboard-reading-within';
  };

  const getBpBand = (val, subtype) => {
    if (val == null) return 'within';
    const v = Number(val);
    if (subtype === 'sys') {
      if (v > 140) return 'high';
      if (v < 100) return 'low';
    } else if (subtype === 'dia') {
      if (v > 90) return 'high';
      if (v < 60) return 'low';
    }
    return 'within';
  };

  const getBgBand = (v) => {
    if (v == null) return 'within';
    if (Number(v) > 110) return 'high';
    if (Number(v) < 60) return 'low';
    return 'within';
  };

  const getWtBand = (weightLb) => {
    if (weightLb == null || Number.isNaN(Number(weightLb))) return 'within';
    if (Number(weightLb) > 220) return 'high';
    if (Number(weightLb) < 66) return 'low';
    return 'within';
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="patient-dashboard-status-badge discontinue">N/A</span>;
    const map = {
      active: { cls: 'active', label: 'Active' },
      paused: { cls: 'paused', label: 'Paused' },
      locked: { cls: 'lock', label: 'Locked' },
      discontinue: { cls: 'discontinue', label: 'N/A' },
      inactive: { cls: 'discontinue', label: 'Inactive' }
    };
    const s = map[status.toLowerCase()] || { cls: 'discontinue', label: status };
    return <span className={`patient-dashboard-status-badge ${s.cls}`}>{s.label}</span>;
  };

  const getGenderText = (g) => g === 1 || g === 'Male' ? 'Male'
    : g === 2 || g === 'Female' ? 'Female' : 'N/A';

  const getInitials = (first, last) =>
    ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || 'P';

  // ── Loading / Error states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="patient-dashboard-page">
        <div className="patient-dashboard-loading-container">
          <div className="patient-dashboard-loading-spinner">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
          <div className="patient-dashboard-loading-text">Loading…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-dashboard-page">
        <div className="patient-dashboard-error-container">
          <div className="patient-dashboard-error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!summary?.patient) {
    return (
      <div className="patient-dashboard-page">
        <div className="patient-dashboard-error-container">
          <div className="patient-dashboard-error-message">No patient data available</div>
        </div>
      </div>
    );
  }

  // ── Destructure summary ──────────────────────────────────────────────────────
  const { patient, latest_vitals, notes_count, upcoming_events_count } = summary;
  const patientView = { ...patient, ...(patientProfile || {}) };
  const bp = latest_vitals?.blood_pressure;
  const bg = latest_vitals?.blood_glucose;
  const weight = latest_vitals?.weight;

  const patientFullName = `${patientView.last_name || ''}, ${patientView.first_name || ''}`.trim();
  const initials = getInitials(patientView.first_name, patientView.last_name);

  const bpHistoryRows = historyBp.slice(0, VITALS_HISTORY_ROW_LIMIT);
  const bgHistoryRows = historyBg.slice(0, VITALS_HISTORY_ROW_LIMIT);
  const wtHistoryRows = historyWt.slice(0, VITALS_HISTORY_ROW_LIMIT);

  const bpPeriodized = getPeriodizedRows(bpHistoryRows, BP_PERIODS);
  const bgPeriodized = getPeriodizedRows(bgHistoryRows, BG_PERIODS);
  const wtPeriodized = getPeriodizedRows(wtHistoryRows, WT_PERIODS);

  const buildExportRows = () => {
    const rows = [];
    if (bp) {
      rows.push({
        type: 'Blood Pressure',
        measured_at: formatDateTime(bp.measured_at),
        value: `${Math.round(bp.systolic)} / ${Math.round(bp.diastolic)}`,
        unit: 'mmHg'
      });
    }
    if (bg) {
      rows.push({
        type: 'Blood Glucose',
        measured_at: formatDateTime(bg.measured_at),
        value: `${Math.round(bg.value)}`,
        unit: 'mg/dl'
      });
    }
    if (weight) {
      rows.push({
        type: 'Weight',
        measured_at: formatDateTime(weight.measured_at),
        value: `${parseFloat(weight.value).toFixed(1)}`,
        unit: 'lb'
      });
    }
    return rows;
  };

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info('No readings available to export');
      return;
    }
    const headers = ['Patient Name', 'Type', 'Measured At', 'Value', 'Unit'];
    const csvLines = [
      headers.join(','),
      ...rows.map((r) =>
        [`"${patientFullName}"`, `"${r.type}"`, `"${r.measured_at}"`, `"${r.value}"`, `"${r.unit}"`].join(',')
      )
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `patient-readings-${patient.id || 'export'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openPrintableWindow = (title = 'Patient Readings Export') => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info('No readings available to export');
      return null;
    }
    const htmlRows = rows
      .map((r) => `<tr><td>${r.type}</td><td>${r.measured_at}</td><td>${r.value}</td><td>${r.unit}</td></tr>`)
      .join('');
    const popup = window.open('', '_blank', 'width=900,height=700');
    if (!popup) {
      toast.error('Popup blocked by browser. Please allow popups to export.');
      return null;
    }
    popup.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body>
          <h2>${title}</h2>
          <p><strong>Patient:</strong> ${patientFullName}</p>
          <table border="1" cellspacing="0" cellpadding="8" style="border-collapse:collapse;width:100%">
            <thead><tr><th>Type</th><th>Measured At</th><th>Value</th><th>Unit</th></tr></thead>
            <tbody>${htmlRows}</tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    return popup;
  };

  const handlePrint = () => {
    const popup = openPrintableWindow('Patient Readings');
    if (!popup) return;
    popup.focus();
    popup.print();
  };

  const handleExportPdf = () => {
    const popup = openPrintableWindow('Patient Readings PDF Export');
    if (!popup) return;
    popup.focus();
    popup.print();
  };

  const handleEmailExport = () => {
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info('No readings available to email');
      return;
    }
    const body = rows
      .map((r) => `${r.type}: ${r.value} ${r.unit} (${r.measured_at})`)
      .join('\n');
    const subject = encodeURIComponent(`Patient Readings - ${patientFullName}`);
    const mailBody = encodeURIComponent(`Patient: ${patientFullName}\n\n${body}`);
    window.location.href = `mailto:?subject=${subject}&body=${mailBody}`;
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="patient-dashboard-page">
      <div className="patient-dashboard-main-container">
        {practiceIsLocked && (
          <div className="patient-dashboard-practice-locked-banner" role="status">
            Your practice is locked. You can review readings for up to 30 days; new readings cannot be added. Export or
            print anything you need to keep.
          </div>
        )}
        <div className="patient-dashboard-content-grid">

          {/* ── Left Column ─────────────────────────────────────────────── */}
          <div className="patient-dashboard-left-column">

            {/* Profile + Details */}
            <div className="patient-dashboard-top-section">
              {/* Profile Card */}
              <div className="patient-dashboard-profile-card">
                <div className="patient-dashboard-profile-header">
                  <div className="patient-dashboard-avatar-circle" style={{ display: 'flex' }}>
                    {initials}
                  </div>
                  <div className="patient-dashboard-profile-info">
                    <div className="patient-dashboard-profile-name-row">
                      <h2 className="patient-dashboard-patient-name-responsive">
                        {patientFullName || 'Patient'}
                      </h2>
                    </div>
                    <p className="patient-dashboard-info-row">
                      <span className="patient-dashboard-info-text-bold">{patientView.phone_number || '-'}</span>
                    </p>
                    <p className="patient-dashboard-info-row">
                      <span className="patient-dashboard-info-text-bold">{patientView.email || user?.email || '-'}</span>
                    </p>
                    <p className="patient-dashboard-info-row">
                      <span className="patient-dashboard-info-text-bold">
                        {patientView.zip_code ? `ZIP: ${patientView.zip_code}` : '-'}
                      </span>
                    </p>
                    <div className="patient-dashboard-status-row">
                      <span>Status:</span>
                      {getStatusBadge(patientView.status)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Card */}
              <div className="patient-dashboard-details-card">
                <div className="patient-dashboard-details-grid">
                  <DetailItem label="Patient ID" value={patientView.patient_id || patientView.id || '-'} />
                  <DetailItem label="ID" value={patientView.id || '-'} />
                  <DetailItem label="DOB" value={formatDate(patientView.date_of_birth)} />
                  <DetailItem label="Gender" value={getGenderText(patientView.gender)} />
                  <DetailItem label="Practice" value={patientView.practice_name || '-'} />
                  <DetailItem label="Provider" value={patientView.provider_name || '-'} />
                  <DetailItem label="Caregiver" value={patientView.caregiver_name || '-'} />
                  <DetailItem label="Phone" value={patientView.phone_number || '-'} />
                  <DetailItem label="Email" value={patientView.email || '-'} />
                  <DetailItem label="Address" value={patientView.full_address || patientView.address_line_1 || '-'} />
                  <DetailItem label="City" value={patientView.city || '-'} />
                  <DetailItem label="State" value={patientView.state || '-'} />
                  <DetailItem label="ZIP" value={patientView.zip_code || '-'} />
                  {patientView.rpm_start_date && (
                    <DetailItem label="RPM Start Date" value={formatDate(patientView.rpm_start_date)} />
                  )}
                </div>
              </div>
            </div>

            {/* Last Upload Data */}
            <div className="patient-dashboard-card">
              <div className="patient-dashboard-last-upload-header">
                <h3 className="mb-0">Last Upload Data</h3>
                <div className="patient-dashboard-actions-right">
                  <button type="button" className="patient-dashboard-brand-btn" onClick={handleExportCsv}>
                    <FontAwesomeIcon icon={faFileCsv} className="me-1" /> CSV
                  </button>
                  <button type="button" className="patient-dashboard-brand-btn" onClick={handleExportPdf}>
                    <FontAwesomeIcon icon={faFilePdf} className="me-1" /> PDF
                  </button>
                  <button type="button" className="patient-dashboard-brand-btn" onClick={handlePrint}>
                    <FontAwesomeIcon icon={faPrint} className="me-1" /> Print
                  </button>
                  <button type="button" className="patient-dashboard-brand-btn" onClick={handleEmailExport}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Email
                  </button>
                </div>
              </div>
              <div className="patient-dashboard-vitals-grid">
                <VitalCard
                  title="Blood Pressure"
                  date={bp ? formatDateTime(bp.measured_at) : ''}
                  systolic={bp ? Math.round(bp.systolic) : null}
                  diastolic={bp ? Math.round(bp.diastolic) : null}
                  unit="mmHg"
                  pulse={bp?.pulse ? Math.round(bp.pulse) : null}
                  noData={!bp}
                  getReadingClass={getReadingBandClass}
                />
                <VitalCard
                  title="Blood Glucose"
                  date={bg ? formatDateTime(bg.measured_at) : ''}
                  value={bg ? Math.round(bg.value) : null}
                  unit="mg/dl"
                  pulse={null}
                  noData={!bg}
                  getReadingClass={(v) => getReadingBandClass(v, 'bg')}
                />
                <VitalCard
                  title="Weight"
                  date={weight ? formatDateTime(weight.measured_at) : ''}
                  value={weight ? parseFloat(weight.value).toFixed(1) : null}
                  unit="lb"
                  pulse={null}
                  noData={!weight}
                  getReadingClass={(v) => getReadingBandClass(v, 'weight')}
                />
              </div>
            </div>

            {/* Recent vitals (30 days) — tabbed tables */}
            <div className="patient-dashboard-card patient-dashboard-vitals-history-card">
              <h3 className="mb-3">Recent readings (last 30 days)</h3>
              <div className="patient-dashboard-vitals-tab-bar" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={vitalsTab === 'bp'}
                  className={`patient-dashboard-vitals-tab${vitalsTab === 'bp' ? ' patient-dashboard-vitals-tab--active' : ''}`}
                  onClick={() => setVitalsTab('bp')}
                >
                  Blood Pressure
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={vitalsTab === 'bg'}
                  className={`patient-dashboard-vitals-tab${vitalsTab === 'bg' ? ' patient-dashboard-vitals-tab--active' : ''}`}
                  onClick={() => setVitalsTab('bg')}
                >
                  Blood Glucose
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={vitalsTab === 'weight'}
                  className={`patient-dashboard-vitals-tab${vitalsTab === 'weight' ? ' patient-dashboard-vitals-tab--active' : ''}`}
                  onClick={() => setVitalsTab('weight')}
                >
                  Weight
                </button>
              </div>

              {historyLoading ? (
                <div className="patient-dashboard-vitals-history-loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading readings…
                </div>
              ) : vitalsTab === 'bp' ? (
                <>
                  <div className="patient-dashboard-vitals-table-wrap">
                    <table className="patient-dashboard-vitals-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Measurement<br />Date</th>
                          {BP_PERIODS.map((p) => (
                            <th key={p.name} colSpan="4">
                              {p.name}
                              <br />
                              {p.start} - {p.end}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {BP_PERIODS.map((p) => (
                            <React.Fragment key={`${p.name}-sub`}>
                              <th>Systolic</th>
                              <th>Diastolic</th>
                              <th>Pulse</th>
                              <th>Measure Time</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bpPeriodized.dates.length === 0 ? (
                          <tr>
                            <td colSpan={BP_PERIODS.length * 4 + 1} className="patient-dashboard-vitals-empty">
                              No readings in this period.
                            </td>
                          </tr>
                        ) : (
                          bpPeriodized.dates.map((date) => (
                            <tr key={date}>
                              <td>{formatDateCell(date)}</td>
                              {BP_PERIODS.map((p) => {
                                const row = bpPeriodized.map[date]?.[p.name]?.[0];
                                const bpBand = getBpBand(row?.systolic_pressure, row?.diastolic_pressure);
                                return (
                                  <React.Fragment key={`${date}-${p.name}`}>
                                    <td className={getReadingBandClass(row?.systolic_pressure, 'bp', 'sys')}>{row?.systolic_pressure != null ? Math.round(row.systolic_pressure) : ''}</td>
                                    <td className={getReadingBandClass(row?.diastolic_pressure, 'bp', 'dia')}>{row?.diastolic_pressure != null ? Math.round(row.diastolic_pressure) : ''}</td>
                                    <td className={getReadingBandClass(row?.pulse, 'bp', 'pulse')}>
                                      {row?.pulse != null ? (
                                        <>
                                          {(Number(row.pulse) < 60 || Number(row.pulse) > 100) ? (
                                            <img src={pulseIcon} alt="pulse" className="patient-dashboard-pulse-inline" />
                                          ) : null}
                                          {Math.round(row.pulse)}
                                        </>
                                      ) : ''}
                                    </td>
                                    <td>{row?.measurement_time || ''}</td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="patient-dashboard-vitals-summary-table-wrap">
                    <table className="patient-dashboard-vitals-summary-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Summary<br />Statistic</th>
                          {BP_PERIODS.map((p) => (
                            <th key={`sum-${p.name}`} colSpan="3">
                              {p.name}
                              <br />
                              {p.start} - {p.end}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {BP_PERIODS.map((p) => (
                            <React.Fragment key={`sum-sub-${p.name}`}>
                              <th>Systolic</th>
                              <th>Diastolic</th>
                              <th>Pulse</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map((stat) => (
                          <tr key={`bp-${stat}`}>
                            <td>{stat}</td>
                            {BP_PERIODS.map((p) => {
                              const values = (bpHistoryRows || []).filter((r) => {
                                const n = r.period_name
                                  ? (BP_PERIODS.find((x) => normalizePeriod(x.name) === normalizePeriod(r.period_name))?.name || r.period_name)
                                  : resolvePeriodByTime(r.measurement_time, BP_PERIODS);
                                return n === p.name;
                              });
                              const avg = (key) =>
                                values.length ? Math.round(values.reduce((a, r) => a + Number(r[key] || 0), 0) / values.length) : '';
                              const max = (key) =>
                                values.length ? Math.round(Math.max(...values.map((r) => Number(r[key] || 0)))) : '';
                              const min = (key) =>
                                values.length ? Math.round(Math.min(...values.map((r) => Number(r[key] || 0)))) : '';
                              const periodCounts = values.reduce(
                                (acc, r) => {
                                  const sBand = getBpBand(r.systolic_pressure, 'sys');
                                  const dBand = getBpBand(r.diastolic_pressure, 'dia');
                                  if (sBand === 'high') acc.sHigh += 1;
                                  else if (sBand === 'low') acc.sLow += 1;
                                  else acc.sWithin += 1;
                                  
                                  if (dBand === 'high') acc.dHigh += 1;
                                  else if (dBand === 'low') acc.dLow += 1;
                                  else acc.dWithin += 1;
                                  
                                  return acc;
                                },
                                { sHigh: 0, sLow: 0, sWithin: 0, dHigh: 0, dLow: 0, dWithin: 0 }
                              );
                              return (
                                <React.Fragment key={`${stat}-${p.name}`}>
                                  <td>{stat === 'Average' ? avg('systolic_pressure') : stat === 'Maximum' ? max('systolic_pressure') : stat === 'Minimum' ? min('systolic_pressure') : stat === 'Count' ? values.length : stat === 'Above Target' ? periodCounts.sHigh : stat === 'Below Target' ? periodCounts.sLow : periodCounts.sWithin}</td>
                                  <td>{stat === 'Average' ? avg('diastolic_pressure') : stat === 'Maximum' ? max('diastolic_pressure') : stat === 'Minimum' ? min('diastolic_pressure') : stat === 'Above Target' ? periodCounts.dHigh : stat === 'Below Target' ? periodCounts.dLow : periodCounts.dWithin}</td>
                                  <td>{stat === 'Average' ? avg('pulse') : stat === 'Maximum' ? max('pulse') : stat === 'Minimum' ? min('pulse') : ''}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                        <tr className="patient-dashboard-summary-total-row">
                          <td>Total</td>
                          <td colSpan={BP_PERIODS.length * 3}>
                            {(() => {
                              const sVals = bpHistoryRows.map(r => r.systolic_pressure).filter(v => v != null && v !== '');
                              const dVals = bpHistoryRows.map(r => r.diastolic_pressure).filter(v => v != null && v !== '');
                              const sTotal = sVals.length;
                              const dTotal = dVals.length;
                              
                              const sHigh = sVals.filter(v => getBpBand(v, 'sys') === 'high').length;
                              const sLow = sVals.filter(v => getBpBand(v, 'sys') === 'low').length;
                              const sWithin = Math.max(sTotal - sHigh - sLow, 0);
                              
                              const dHigh = dVals.filter(v => getBpBand(v, 'dia') === 'high').length;
                              const dLow = dVals.filter(v => getBpBand(v, 'dia') === 'low').length;
                              const dWithin = Math.max(dTotal - dHigh - dLow, 0);
                              
                              const pct = (n, t) => (t ? `${Math.round((n * 100) / t)}% (${n})` : '0% (0)');
                              
                              return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                  <div>
                                    <strong>Systolic:</strong> Above: {pct(sHigh, sTotal)} | Below: {pct(sLow, sTotal)} | Within: {pct(sWithin, sTotal)}
                                  </div>
                                  <div style={{ color: '#666' }}>|</div>
                                  <div>
                                    <strong>Diastolic:</strong> Above: {pct(dHigh, dTotal)} | Below: {pct(dLow, dTotal)} | Within: {pct(dWithin, dTotal)}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : vitalsTab === 'bg' ? (
                <>
                  <div className="patient-dashboard-vitals-table-wrap">
                    <table className="patient-dashboard-vitals-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Measurement<br />Date</th>
                          {BG_PERIODS.map((p) => (
                            <th key={p.name} colSpan="2">
                              {p.name}
                              <br />
                              {p.start} - {p.end}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {BG_PERIODS.map((p) => (
                            <React.Fragment key={`${p.name}-bg-sub`}>
                              <th>Blood Glucose</th>
                              <th>Measure Time</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bgPeriodized.dates.length === 0 ? (
                          <tr>
                            <td colSpan={BG_PERIODS.length * 2 + 1} className="patient-dashboard-vitals-empty">
                              No readings in this period.
                            </td>
                          </tr>
                        ) : (
                          bgPeriodized.dates.map((date) => (
                            <tr key={`bg-${date}`}>
                              <td>{formatDateCell(date)}</td>
                              {BG_PERIODS.map((p) => {
                                const row = bgPeriodized.map[date]?.[p.name]?.[0];
                                const v = row?.blood_glucose_value ?? row?.glucose_value ?? row?.blood_glucose_value_1;
                                const bgBand = getBgBand(v);
                                return (
                                  <React.Fragment key={`bg-${date}-${p.name}`}>
                                    <td className={getBandClass(bgBand)}>{v != null ? Math.round(Number(v)) : ''}</td>
                                    <td>{row?.measurement_time || ''}</td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="patient-dashboard-vitals-summary-table-wrap">
                    <table className="patient-dashboard-vitals-summary-table">
                      <thead>
                        <tr>
                          <th>Summary Statistic</th>
                          {BG_PERIODS.map((p) => (
                            <th key={`bg-s-${p.name}`}>{p.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map((stat) => (
                          <tr key={`bg-${stat}`}>
                            <td>{stat}</td>
                            {BG_PERIODS.map((p) => {
                              const values = bgHistoryRows
                                .filter((r) => {
                                  const n = r.period_name
                                    ? (BG_PERIODS.find((x) => normalizePeriod(x.name) === normalizePeriod(r.period_name))?.name || r.period_name)
                                    : resolvePeriodByTime(r.measurement_time, BG_PERIODS);
                                  return n === p.name;
                                })
                                .map((r) => Number(r.blood_glucose_value ?? r.glucose_value ?? r.blood_glucose_value_1))
                                .filter((v) => !Number.isNaN(v));
                              const counts = values.reduce(
                                (acc, v) => {
                                  const b = getBgBand(v);
                                  if (b === 'high') acc.high += 1;
                                  else if (b === 'low') acc.low += 1;
                                  else acc.within += 1;
                                  return acc;
                                },
                                { high: 0, low: 0, within: 0 }
                              );
                              const val =
                                stat === 'Average' ? (values.length ? Math.round(values.reduce((a, v) => a + v, 0) / values.length) : '') :
                                  stat === 'Maximum' ? (values.length ? Math.round(Math.max(...values)) : '') :
                                    stat === 'Minimum' ? (values.length ? Math.round(Math.min(...values)) : '') :
                                      stat === 'Count' ? values.length :
                                        stat === 'Above Target' ? counts.high :
                                          stat === 'Below Target' ? counts.low : counts.within;
                              return <td key={`bg-${stat}-${p.name}`}>{val}</td>;
                            })}
                          </tr>
                        ))}
                        <tr className="patient-dashboard-summary-total-row">
                          <td>Total</td>
                          <td colSpan={BG_PERIODS.length}>
                            {(() => {
                              const vals = bgHistoryRows
                                .map((r) => Number(r.blood_glucose_value ?? r.glucose_value ?? r.blood_glucose_value_1))
                                .filter((v) => !Number.isNaN(v));
                              const total = vals.length;
                              const high = vals.filter((v) => getBgBand(v) === 'high').length;
                              const low = vals.filter((v) => getBgBand(v) === 'low').length;
                              const within = Math.max(total - high - low, 0);
                              const pct = (n) => (total ? `${Math.round((n * 100) / total)}% (${n})` : '0% (0)');
                              return `Above target: ${pct(high)} | Below target: ${pct(low)} | Within target: ${pct(within)}`;
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="patient-dashboard-vitals-table-wrap">
                    <table className="patient-dashboard-vitals-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Measurement<br />Date</th>
                          {WT_PERIODS.map((p) => (
                            <th key={p.name} colSpan="4">
                              {p.name}
                              <br />
                              {p.start} - {p.end}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {WT_PERIODS.map((p) => (
                            <React.Fragment key={`${p.name}-wt-sub`}>
                              <th>Weight</th>
                              <th>BMI</th>
                              <th>Body fat</th>
                              <th>Measure Time</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {wtPeriodized.dates.length === 0 ? (
                          <tr>
                            <td colSpan={WT_PERIODS.length * 4 + 1} className="patient-dashboard-vitals-empty">
                              No readings in this period.
                            </td>
                          </tr>
                        ) : (
                          wtPeriodized.dates.map((date) => (
                            <tr key={`wt-${date}`}>
                              <td>{formatDateCell(date)}</td>
                              {WT_PERIODS.map((p) => {
                                const row = wtPeriodized.map[date]?.[p.name]?.[0];
                                const lb =
                                  row?.weight != null && !Number.isNaN(Number(row.weight))
                                    ? (Number(row.weight) * 2.20462).toFixed(1)
                                    : '';
                                const wtBand = getWtBand(lb);
                                return (
                                  <React.Fragment key={`wt-${date}-${p.name}`}>
                                    <td className={getBandClass(wtBand)}>{lb}</td>
                                    <td>{row?.bmi != null ? Number(row.bmi).toFixed(1) : ''}</td>
                                    <td>{row?.body_fat != null ? Number(row.body_fat).toFixed(1) : ''}</td>
                                    <td>{row?.measurement_time || ''}</td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="patient-dashboard-vitals-summary-table-wrap">
                    <table className="patient-dashboard-vitals-summary-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">Summary Statistic</th>
                          {WT_PERIODS.map((p) => (
                            <th key={`wt-s-${p.name}`} colSpan="3">{p.name}</th>
                          ))}
                        </tr>
                        <tr>
                          {WT_PERIODS.map((p) => (
                            <React.Fragment key={`wt-s-sub-${p.name}`}>
                              <th>Weight</th>
                              <th>BMI</th>
                              <th>Body fat</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map((stat) => (
                          <tr key={`wt-${stat}`}>
                            <td>{stat}</td>
                            {WT_PERIODS.map((p) => {
                              const values = wtHistoryRows.filter((r) => {
                                const n = r.period_name
                                  ? (WT_PERIODS.find((x) => normalizePeriod(x.name) === normalizePeriod(r.period_name))?.name || r.period_name)
                                  : resolvePeriodByTime(r.measurement_time, WT_PERIODS);
                                return n === p.name;
                              });
                              const get = (key, mul = 1) =>
                                values.map((r) => Number(r[key] || 0) * mul).filter((v) => !Number.isNaN(v));
                              const w = get('weight', 2.20462);
                              const b = get('bmi');
                              const f = values.map((r) => Number(r.body_fat || 0)).filter((v) => !Number.isNaN(v));
                              const wb = w.reduce(
                                (acc, v) => {
                                  const b = getWtBand(v);
                                  if (b === 'high') acc.high += 1;
                                  else if (b === 'low') acc.low += 1;
                                  else acc.within += 1;
                                  return acc;
                                },
                                { high: 0, low: 0, within: 0 }
                              );
                              const pick = (arr) =>
                                stat === 'Average' ? (arr.length ? (arr.reduce((a, v) => a + v, 0) / arr.length).toFixed(1) : '') :
                                  stat === 'Maximum' ? (arr.length ? Math.max(...arr).toFixed(1) : '') :
                                    stat === 'Minimum' ? (arr.length ? Math.min(...arr).toFixed(1) : '') :
                                      stat === 'Count' ? values.length :
                                        stat === 'Above Target' ? wb.high :
                                          stat === 'Below Target' ? wb.low : wb.within;
                              return (
                                <React.Fragment key={`wt-${stat}-${p.name}`}>
                                  <td>{pick(w)}</td>
                                  <td>{stat === 'Count' ? '' : pick(b)}</td>
                                  <td>{stat === 'Count' ? '' : pick(f)}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                        <tr className="patient-dashboard-summary-total-row">
                          <td>Total</td>
                          <td colSpan={WT_PERIODS.length * 3}>
                            {(() => {
                              const vals = wtHistoryRows
                                .map((r) => (r.weight != null ? Number(r.weight) * 2.20462 : NaN))
                                .filter((v) => !Number.isNaN(v));
                              const total = vals.length;
                              const high = vals.filter((v) => getWtBand(v) === 'high').length;
                              const low = vals.filter((v) => getWtBand(v) === 'low').length;
                              const within = Math.max(total - high - low, 0);
                              const pct = (n) => (total ? `${Math.round((n * 100) / total)}% (${n})` : '0% (0)');
                              return `Above target: ${pct(high)} | Below target: ${pct(low)} | Within target: ${pct(within)}`;
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Right Column ─────────────────────────────────────────────── */}
          <div className="patient-dashboard-right-column">

            {/* Notes — always visible */}
            <div className="patient-dashboard-card patient-dashboard-notes-card">
              <div className="patient-dashboard-notes-header patient-dashboard-notes-header--static">
                <h3 className="mb-0">
                  Notes
                  {notes_count > 0 && <span className="patient-dashboard-notes-badge">{notes_count}</span>}
                </h3>
              </div>

              <div className="patient-dashboard-notes-list">
                {notesLoading ? (
                  <div className="patient-dashboard-loading-container" style={{ padding: '1rem' }}>
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading notes…
                  </div>
                ) : notes.length > 0 ? (
                  notes.map((note, idx) => (
                    <NoteItem
                      key={idx}
                      author={`${note.last_name || ''}, ${note.first_name || ''}`.trim() || 'Unknown'}
                      date={formatDateShort(note.date || note.created_at)}
                      text={note.content || ''}
                    />
                  ))
                ) : (
                  <div className="patient-dashboard-empty-notes">
                    <p>No notes available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Events summary badge */}
            {upcoming_events_count > 0 && (
              <div className="patient-dashboard-card" style={{ marginTop: '0.75rem' }}>
                <h3>Upcoming Events</h3>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted, #666)' }}>
                  You have <strong>{upcoming_events_count}</strong> upcoming event{upcoming_events_count !== 1 ? 's' : ''}.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function DetailItem({ label, value }) {
  return (
    <div className="patient-dashboard-detail-item">
      <span className="patient-dashboard-detail-label">{label}</span>
      <span className="patient-dashboard-detail-colon">:</span>
      <span className="patient-dashboard-detail-value">{value}</span>
    </div>
  );
}

function VitalCard({ title, date, value, systolic, diastolic, unit, pulse, noData, getReadingClass }) {
  const isBP = title === 'Blood Pressure';
  
  return (
    <div className={`patient-dashboard-vital-card ${noData ? '' : 'patient-dashboard-vital-card-clickable'}`}>
      <div className="patient-dashboard-vital-header">
        {title}
        {date && <div className="patient-dashboard-vital-date">{date}</div>}
      </div>
      <div className="patient-dashboard-vital-body">
        {noData ? (
          <p className="patient-dashboard-vital-value-no-data">No data</p>
        ) : (
          <>
            <div className="patient-dashboard-vital-value-container">
              {isBP ? (
                <p className="patient-dashboard-vital-value">
                  <span className={getReadingClass ? getReadingClass(systolic, 'bp', 'sys') : ''}>{systolic}</span>
                  <span className="patient-dashboard-vital-separator"> / </span>
                  <span className={getReadingClass ? getReadingClass(diastolic, 'bp', 'dia') : ''}>{diastolic}</span>
                </p>
              ) : (
                <p className={`patient-dashboard-vital-value ${getReadingClass ? getReadingClass(value) : ''}`}>
                  {value}
                </p>
              )}
            </div>
            <p className="patient-dashboard-vital-unit">{unit}</p>
            {pulse && (
              <div className="patient-dashboard-pulse-info">
                Pulse <span className="patient-dashboard-pulse-value">{pulse}</span>
              </div>
            )}
          </>
        )}
      </div>
      <div className="patient-dashboard-vital-footer">
        {noData && <span className="patient-dashboard-no-data-text">No data</span>}
      </div>
    </div>
  );
}

function NoteItem({ author, date, text }) {
  return (
    <div className="patient-dashboard-note-item">
      <div className="patient-dashboard-note-meta">
        <span>{author}</span>
        <span className="patient-dashboard-note-date">{date}</span>
      </div>
      <p>{text}</p>
    </div>
  );
}

export default PatientDashboard;
